/** @format */

// Landing-page "Did you know?" rails — one rotating fact card per side.
//
// Data comes from GET /api/proship/facts (see fetchMyFacts in propilotApi):
// ten pre-computed facts about the merchant's last-30-days shipping, cached
// server-side per merchant (0 Claude calls). Facts carry `rail`
// ("performance" → left, "network" → right) and a `viz` block this component
// knows how to draw (bars / donut / big_vs / split / spark / weekday).
//
// Behaviour mirrors the approved mockup:
//   - one card visible per rail, auto-advances every 10s
//   - colored progress bar along the card bottom fills over the interval
//   - hovering pauses the timer (and the bar)
//   - dots underneath jump to a specific fact
//   - the right rail is offset 5s from the left so they never flip together
//   - clicking a card fires its `question` into the chat

import React from "react";
import { Box, ButtonBase, Typography } from "@mui/material";

const ROTATE_MS = 10000;

// Per-fact visual identity. Unknown keys fall back to indigo so a new fact
// added server-side still renders.
const FACT_THEMES = {
  best_courier: { icon: "🏆", accent: "#059669", bg: "linear-gradient(135deg,#ecfdf5,#d1fae5)", border: "#a7f3d0" },
  cod_prepaid_split: { icon: "💰", accent: "#7c3aed", bg: "linear-gradient(135deg,#f5f3ff,#ede9fe)", border: "#ddd6fe" },
  rto_by_payment: { icon: "🔄", accent: "#d97706", bg: "linear-gradient(135deg,#fffbeb,#fef3c7)", border: "#fde68a" },
  avg_tat: { icon: "⚡", accent: "#2563eb", bg: "linear-gradient(135deg,#eff6ff,#dbeafe)", border: "#bfdbfe" },
  top_ndr_reason: { icon: "❗", accent: "#dc2626", bg: "linear-gradient(135deg,#fef2f2,#fee2e2)", border: "#fecaca" },
  top_pincodes: { icon: "📍", accent: "#0891b2", bg: "linear-gradient(135deg,#ecfeff,#cffafe)", border: "#a5f3fc" },
  volume_trend: { icon: "📈", accent: "#16a34a", bg: "linear-gradient(135deg,#f0fdf4,#dcfce7)", border: "#bbf7d0" },
  zone_split: { icon: "🗺️", accent: "#9333ea", bg: "linear-gradient(135deg,#faf5ff,#f3e8ff)", border: "#e9d5ff" },
  top_cities: { icon: "🏙️", accent: "#ea580c", bg: "linear-gradient(135deg,#fff7ed,#ffedd5)", border: "#fed7aa" },
  busiest_day: { icon: "📅", accent: "#6366f1", bg: "linear-gradient(135deg,#eef2ff,#e0e7ff)", border: "#c7d2fe" },
};
const DEFAULT_THEME = FACT_THEMES.busiest_day;

const themeFor = (key) => FACT_THEMES[key] || DEFAULT_THEME;

// Lighter shade of the accent for ranked-bar gradients (rank 0 = full).
const shade = (accent, rank) => {
  const alpha = Math.max(0.25, 1 - rank * 0.18);
  return `color-mix(in srgb, ${accent} ${Math.round(alpha * 100)}%, white)`;
};

// ── Mini-viz renderers ────────────────────────────────────────────────────────

const RankedBars = ({ items, accent }) => {
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.9, mt: 1.25, width: "100%" }}>
      {items.map((item, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
          <Typography
            sx={{
              width: 72, fontSize: 11, fontWeight: 700, color: "#475569",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              flexShrink: 0, textAlign: "left",
            }}
          >
            {item.name}
          </Typography>
          <Box sx={{ flex: 1, height: 9, borderRadius: "5px", backgroundColor: "rgba(255,255,255,0.7)", overflow: "hidden" }}>
            <Box
              sx={{
                width: `${(item.value / max) * 100}%`, height: "100%",
                borderRadius: "5px", backgroundColor: shade(accent, i),
              }}
            />
          </Box>
          <Typography sx={{ width: 44, fontSize: 11, fontWeight: 800, color: "#0f172a", textAlign: "right", flexShrink: 0 }}>
            {item.display != null ? item.display : item.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const Donut = ({ items, accent }) => {
  const primary = items[0] || { name: "", pct: 0, value: 0 };
  const secondary = items[1] || { name: "", pct: 0, value: 0 };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, mt: 2, width: "100%" }}>
      <Box
        sx={{
          width: 110, height: 110, borderRadius: "50%", position: "relative",
          background: `conic-gradient(${accent} 0 ${primary.pct}%, ${shade(accent, 3)} ${primary.pct}% 100%)`,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute", inset: "22px", borderRadius: "50%", backgroundColor: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, fontWeight: 800, color: accent,
          }}
        >
          {primary.pct}%
        </Box>
      </Box>
      <Box sx={{ fontSize: 12, color: "#475569", fontWeight: 600, lineHeight: 1.9 }}>
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", mr: 1.5 }}>
          <Box sx={{ width: 9, height: 9, borderRadius: "3px", backgroundColor: accent, mr: 0.6 }} />
          {primary.name} <b style={{ marginLeft: 4 }}>{Number(primary.value).toLocaleString()}</b>
        </Box>
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
          <Box sx={{ width: 9, height: 9, borderRadius: "3px", backgroundColor: shade(accent, 3), mr: 0.6 }} />
          {secondary.name} <b style={{ marginLeft: 4 }}>{Number(secondary.value).toLocaleString()}</b>
        </Box>
      </Box>
    </Box>
  );
};

const BigVs = ({ left, right, accent }) => (
  <Box sx={{ mt: 1, width: "100%" }}>
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
      <Typography sx={{ fontSize: 28, fontWeight: 800, color: accent }}>{left.rate}%</Typography>
      <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "#64748b" }}>
        {left.name} vs {right.name}
      </Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 800, color: "#059669" }}>{right.rate}%</Typography>
    </Box>
    <RankedBars
      accent={accent}
      items={[
        { name: left.name, value: left.count, display: Number(left.count).toLocaleString() },
        { name: right.name, value: right.count, display: Number(right.count).toLocaleString() },
      ]}
    />
  </Box>
);

const SplitBar = ({ items, accent }) => {
  const total = items.reduce((s, i) => s + i.value, 0) || 1;
  return (
    <Box sx={{ mt: 1.25, width: "100%" }}>
      <Box sx={{ display: "flex", height: 12, borderRadius: "6px", overflow: "hidden", boxShadow: "inset 0 1px 2px rgba(15,23,42,0.08)" }}>
        {items.map((item, i) => (
          <Box key={i} sx={{ width: `${(item.value / total) * 100}%`, backgroundColor: shade(accent, i) }} />
        ))}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.6, mb: 1 }}>
        {items.slice(0, 5).map((item, i) => (
          <Typography key={i} sx={{ fontSize: 10, fontWeight: 700, color: "#475569" }}>
            {item.name} {item.pct}%
          </Typography>
        ))}
      </Box>
      <RankedBars
        accent={accent}
        items={items.slice(0, 5).map((item) => ({
          name: item.name, value: item.value, display: Number(item.value).toLocaleString(),
        }))}
      />
    </Box>
  );
};

const Sparkline = ({ points, accent }) => {
  if (!points || points.length < 2) return null;
  const W = 200;
  const H = 60;
  const max = Math.max(...points, 1);
  const step = W / (points.length - 1);
  const coords = points.map((v, i) => `${(i * step).toFixed(1)},${(H - 6 - (v / max) * (H - 12)).toFixed(1)}`);
  return (
    <Box component="svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" sx={{ width: "100%", height: 130, mt: 1.5 }}>
      <polygon points={`0,${H} ${coords.join(" ")} ${W},${H}`} fill={accent} opacity="0.15" />
      <polyline points={coords.join(" ")} fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" />
    </Box>
  );
};

const WeekdayBars = ({ items, accent }) => {
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <Box sx={{ mt: 1.5, width: "100%" }}>
      <Box sx={{ display: "flex", gap: 0.6, alignItems: "flex-end", height: 80 }}>
        {items.map((item, i) => (
          <Box
            key={i}
            sx={{
              flex: 1, borderRadius: "4px 4px 0 0",
              height: `${Math.max((item.value / max) * 100, 3)}%`,
              backgroundColor: item.value === max ? accent : `${accent}59`,
            }}
          />
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 0.6, mt: 0.4 }}>
        {items.map((item, i) => (
          <Typography key={i} sx={{ flex: 1, textAlign: "center", fontSize: 9.5, fontWeight: 700, color: "#64748b" }}>
            {item.name[0]}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

const FactViz = ({ viz, accent }) => {
  if (!viz) return null;
  switch (viz.type) {
    case "bars":
      return <RankedBars items={viz.items || []} accent={accent} />;
    case "donut":
      return <Donut items={viz.items || []} accent={accent} />;
    case "big_vs":
      return viz.left && viz.right ? <BigVs left={viz.left} right={viz.right} accent={accent} /> : null;
    case "split":
      return <SplitBar items={viz.items || []} accent={accent} />;
    case "spark":
      return <Sparkline points={viz.points} accent={accent} />;
    case "weekday":
      return <WeekdayBars items={viz.items || []} accent={accent} />;
    default:
      return null;
  }
};

// ── Rotating rail ─────────────────────────────────────────────────────────────

const FactCard = ({ fact, onAsk, paused }) => {
  const theme = themeFor(fact.key);
  return (
    <ButtonBase
      onClick={() => onAsk(fact.question)}
      focusRipple
      sx={{
        display: "flex", flexDirection: "column", alignItems: "stretch",
        width: "100%", minHeight: 430, textAlign: "left",
        p: "16px 16px 14px", borderRadius: "16px",
        background: theme.bg, border: `1px solid ${theme.border}`,
        position: "relative", overflow: "hidden",
        transition: "transform 180ms ease, box-shadow 180ms ease",
        animation: "ppFactIn 550ms cubic-bezier(0.21,0.9,0.32,1) backwards",
        "@keyframes ppFactIn": {
          from: { opacity: 0, transform: "translateY(14px) scale(0.97)" },
          to: { opacity: 1, transform: "none" },
        },
        "@keyframes ppFactFill": {
          from: { width: 0 },
          to: { width: "100%" },
        },
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: `0 14px 30px -14px ${theme.accent}99`,
        },
        "&:hover .pp-fact-ask": { backgroundColor: "#0f172a", color: "#fff" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, mb: 1.25 }}>
        <Box
          sx={{
            width: 36, height: 36, borderRadius: "10px", backgroundColor: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 17, flexShrink: 0, boxShadow: "0 3px 8px -3px rgba(15,23,42,0.25)",
          }}
        >
          {theme.icon}
        </Box>
        <Typography
          sx={{
            fontSize: 10.5, fontWeight: 800, letterSpacing: "0.07em",
            textTransform: "uppercase", color: theme.accent, lineHeight: 1.25,
          }}
        >
          {fact.label}
        </Typography>
      </Box>

      <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.015em", lineHeight: 1.2 }}>
        {fact.headline}
      </Typography>
      <Typography sx={{ fontSize: 12, color: "#475569", mt: 0.5, lineHeight: 1.45 }}>
        {fact.sub}
      </Typography>

      <FactViz viz={fact.viz} accent={theme.accent} />

      <Box
        className="pp-fact-ask"
        sx={{
          mt: "auto", alignSelf: "flex-start", px: 1.6, py: 0.7,
          borderRadius: "999px", backgroundColor: "rgba(255,255,255,0.85)",
          boxShadow: "0 3px 10px -4px rgba(15,23,42,0.25)",
          fontSize: 11.5, fontWeight: 800, color: "#334155",
          transition: "all 160ms ease",
        }}
      >
        Ask ProPilot →
      </Box>

      {/* progress to next rotation — pauses with the timer on hover */}
      <Box
        key={fact.key /* restart the fill animation on every card change */}
        sx={{
          position: "absolute", left: 0, bottom: 0, height: 4, width: 0,
          backgroundColor: theme.accent, opacity: 0.55, borderRadius: "0 3px 3px 0",
          animation: `ppFactFill ${ROTATE_MS}ms linear forwards`,
          animationPlayState: paused ? "paused" : "running",
        }}
      />
    </ButtonBase>
  );
};

const FactRail = ({ title, icon, subtitle, facts, onAsk, offsetMs = 0 }) => {
  const [index, setIndex] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const pausedRef = React.useRef(false);
  pausedRef.current = paused;

  React.useEffect(() => {
    if (!facts || facts.length < 2) return undefined;
    let timer = null;
    const start = setTimeout(() => {
      timer = setInterval(() => {
        if (!pausedRef.current) {
          setIndex((prev) => (prev + 1) % facts.length);
        }
      }, ROTATE_MS);
    }, offsetMs);
    return () => {
      clearTimeout(start);
      if (timer) clearInterval(timer);
    };
  }, [facts, offsetMs]);

  if (!facts || facts.length === 0) return null;
  const fact = facts[Math.min(index, facts.length - 1)];

  return (
    <Box
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      sx={{ display: "flex", flexDirection: "column", width: "100%" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, px: 0.25, mb: 1 }}>
        <Typography component="span" sx={{ fontSize: 13 }}>{icon}</Typography>
        <Typography
          sx={{
            fontSize: 10.5, fontWeight: 800, color: "#94a3b8",
            textTransform: "uppercase", letterSpacing: "0.08em",
          }}
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography sx={{ ml: "auto", fontSize: 9.5, fontWeight: 600, color: "#cbd5e1" }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>

      <FactCard key={fact.key} fact={fact} onAsk={onAsk} paused={paused} />

      {facts.length > 1 ? (
        <Box sx={{ display: "flex", gap: 0.6, justifyContent: "center", mt: 1.1 }}>
          {facts.map((f, i) => (
            <Box
              key={f.key}
              onClick={() => setIndex(i)}
              sx={{
                width: i === index ? 18 : 6, height: 6, borderRadius: "999px",
                backgroundColor: i === index ? "#6366f1" : "#cbd5e1",
                cursor: "pointer", transition: "all 250ms ease",
              }}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

// ── Public wrapper: splits facts into the two rails ───────────────────────────

export const splitFactsByRail = (facts) => {
  const list = Array.isArray(facts) ? facts : [];
  return {
    performance: list.filter((f) => f.rail === "performance"),
    network: list.filter((f) => f.rail !== "performance"),
  };
};

export default FactRail;
